import { useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  CalendarCheck,
  Clock,
  CalendarDays,
  ListChecks,
  Vote,
  Compass,
  FolderKanban,
  BarChart3,
  Lightbulb,
  Image as ImageIcon,
  Brain,
  UserCircle2,
  Send,
  TelescopeIcon,
  Users2,
  MessageSquareCode,
  Webhook,
  Plug2,
  ScrollText,
  Building2,
  Shield,
  CreditCard,
  Settings,
  FileText,
  MoreHorizontal,
  ChevronsUpDown,
  Bug,
  Sun,
  Moon,
  LogOut,
  Server,
  Boxes,
  Mail,
  Megaphone,
  Target,
  Sunrise,
  CheckCheck,
  BookOpen,
} from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { Metronome } from './icons/Metronome';

type RailItem = {
  label: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
};

type RailGroup = {
  label: string;
  items: RailItem[];
};

// Tenant rail — grouped + labelled per the redesign IA. Today /
// Work / Horizon / Intelligence as the four primary clusters;
// Capture and the configure-level routes live behind the More
// popover so the rail itself stays focused on the day.
const TENANT_GROUPS: RailGroup[] = [
  {
    label: 'Today',
    items: [
      { label: 'The Brief', path: '/brief', icon: Sunrise },
      { label: 'Dashboard', path: '/', icon: LayoutDashboard },
      { label: 'Plan', path: '/plan', icon: CalendarCheck },
      { label: 'Focus Time', path: '/focus', icon: Clock },
      { label: 'Close Day', path: '/close-day', icon: CheckCheck },
    ],
  },
  {
    label: 'Work',
    items: [
      { label: 'Work Items', path: '/work-items', icon: ListChecks },
      { label: 'Projects', path: '/projects', icon: FolderKanban },
      { label: 'Decisions', path: '/decisions', icon: Vote },
      { label: 'Calendar', path: '/calendar', icon: CalendarDays },
    ],
  },
  {
    label: 'Horizon',
    items: [
      { label: 'Strategic Drivers', path: '/strategy', icon: Compass },
      { label: 'Reflections', path: '/reflections', icon: BookOpen },
    ],
  },
  {
    label: 'Intelligence',
    items: [
      { label: 'Reports', path: '/reports', icon: BarChart3 },
      // Renamed from "Horizon" — the HORIZON group above already
      // contains Strategic Drivers + Reflections; having a second
      // "Horizon" entry adjacent was confusing.
      { label: 'Outlook', path: '/horizon', icon: TelescopeIcon },
      { label: 'Capacity', path: '/capacity', icon: Target },
    ],
  },
];

const TENANT_OVERFLOW_GROUPS: RailGroup[] = [
  {
    label: 'Capture',
    items: [
      { label: 'Transcripts', path: '/transcripts', icon: FileText },
      { label: 'Ideas', path: '/ideas', icon: Lightbulb },
      { label: 'Image Capture', path: '/images', icon: ImageIcon },
      { label: 'Context Engine', path: '/context', icon: Brain },
      { label: 'Principal Profile', path: '/principal-profile', icon: UserCircle2 },
      { label: 'Delegation', path: '/delegation', icon: Send },
      { label: 'EA Workspace', path: '/ea-workspace', icon: Users2 },
    ],
  },
  {
    label: 'Configure',
    items: [
      { label: 'Preferences', path: '/preferences', icon: Settings },
      { label: 'Feature Requests', path: '/feature-requests', icon: Megaphone },
      { label: 'Prompt Management', path: '/prompt-management', icon: MessageSquareCode },
      { label: 'Webhooks', path: '/webhooks', icon: Webhook },
      { label: 'Connections', path: '/connections', icon: Plug2 },
      { label: 'Audit Log', path: '/audit-log', icon: ScrollText },
      { label: 'Organisation', path: '/organisation', icon: Building2 },
      { label: 'Admin', path: '/admin', icon: Shield },
      { label: 'Billing', path: '/billing', icon: CreditCard },
    ],
  },
];

// Platform admin rail — labelled but a flatter IA than the tenant
// rail since the platform side already has fewer pages.
const PLATFORM_GROUPS: RailGroup[] = [
  {
    label: 'Platform',
    items: [
      { label: 'Overview', path: '/platform', icon: LayoutDashboard },
      { label: 'Tenants', path: '/platform/tenants', icon: Building2 },
    ],
  },
  {
    label: 'Billing',
    items: [
      { label: 'Billing Centre', path: '/platform/billing', icon: CreditCard },
      { label: 'Products & Pricing', path: '/platform/products', icon: Boxes },
      { label: 'Email Settings', path: '/platform/email', icon: Mail },
    ],
  },
  {
    label: 'System',
    items: [
      { label: 'Security & Diagnostics', path: '/platform/security', icon: Shield },
      { label: 'Bug Reports', path: '/platform/bug-reports', icon: Bug },
      { label: 'Client Releases', path: '/platform/releases', icon: Server },
    ],
  },
];

const PLATFORM_OVERFLOW_GROUPS: RailGroup[] = [];

const ACCOUNT_ITEM_CLASS =
  'flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

interface SlimRailProps {
  isPlatformAdmin: boolean;
  themeMode: 'dark' | 'light';
  onToggleTheme: () => void;
  onReportBug: () => void;
  onLogout: () => void;
  userInitial: string;
  userDisplayName: string;
  userRole: string;
  // Filter items by role and plan. AppLayout passes the same predicate it
  // uses for the mobile drawer (lib/planGates: hide Transcripts when the plan
  // lacks transcript processing, leadership pages on Personal, etc.) so the
  // visibility table lives in one place.
  isVisible: (path: string) => boolean;
}

/**
 * Labelled + grouped sidebar at 220px. Brand mark at the top,
 * grouped primary navigation, an overflow popover for the secondary
 * routes (Capture + Configure), then a labelled account row pinned at
 * the bottom whose menu holds preferences, theme, support and sign out.
 *
 * The redesign explicitly asked for labelled groups over icon-only
 * (legibility win for execs scanning by name rather than glyph). The
 * old 72px icon-only variant lives in git history; this is the
 * replacement.
 */
export function SlimRail(props: SlimRailProps) {
  const {
    isPlatformAdmin,
    themeMode,
    onToggleTheme,
    onReportBug,
    onLogout,
    userInitial,
    userDisplayName,
    userRole,
    isVisible,
  } = props;

  const location = useLocation();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);

  const primaryGroups = useMemo(() => {
    const groups = isPlatformAdmin ? PLATFORM_GROUPS : TENANT_GROUPS;
    return groups
      .map(g => ({ ...g, items: g.items.filter(it => isVisible(it.path)) }))
      .filter(g => g.items.length > 0);
  }, [isPlatformAdmin, isVisible]);

  const overflowGroups = useMemo(() => {
    const groups = isPlatformAdmin ? PLATFORM_OVERFLOW_GROUPS : TENANT_OVERFLOW_GROUPS;
    return groups
      .map(g => ({ ...g, items: g.items.filter(it => isVisible(it.path)) }))
      .filter(g => g.items.length > 0);
  }, [isPlatformAdmin, isVisible]);

  const [accountOpen, setAccountOpen] = useState(false);
  const accountTriggerRef = useRef<HTMLButtonElement>(null);

  const ThemeIcon = themeMode === 'dark' ? Sun : Moon;
  const themeLabel = themeMode === 'dark' ? 'Switch to light' : 'Switch to dark';

  // Close the account menu before acting. The rail never unmounts, so an
  // uncontrolled menu stays open after navigating to Preferences, and the
  // support dialog would open underneath it. Focus goes back to the row
  // first, so a dialog the action opens has somewhere to return it to —
  // the menu item that was clicked is about to unmount.
  const runAccountAction = (action: () => void) => {
    setAccountOpen(false);
    accountTriggerRef.current?.focus();
    action();
  };

  return (
    <aside
      aria-label="Primary navigation"
      className={cn(
        'fixed inset-y-0 left-0 z-40 hidden w-[220px] flex-col overflow-hidden md:flex',
        // macOS-style sidebar glass in light. In dark the rail is solid
        // slate: a translucent fill over the dark page reads as grey haze,
        // and blurring a flat page costs GPU for no visible effect.
        'border-r border-white/40 dark:border-border',
        'bg-white/45 dark:bg-card',
        'backdrop-blur-2xl backdrop-saturate-150 dark:backdrop-filter-none',
        'shadow-[1px_0_2px_rgba(15,23,42,0.04),12px_0_40px_-20px_rgba(15,23,42,0.12),inset_0_1px_0_rgba(255,255,255,0.7)]',
        'dark:shadow-[1px_0_2px_rgba(0,0,0,0.4),12px_0_40px_-20px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.08)]',
        'before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-32',
        'before:bg-[linear-gradient(to_bottom,rgba(255,255,255,0.5)_0%,rgba(255,255,255,0.12)_50%,rgba(255,255,255,0)_100%)]',
        'after:pointer-events-none after:absolute after:inset-0 after:[mix-blend-mode:plus-lighter]',
        'after:bg-[linear-gradient(135deg,rgba(255,255,255,0.30)_0%,rgba(255,255,255,0.08)_28%,rgba(255,255,255,0)_60%)]',
        'dark:before:hidden dark:after:hidden',
        '[&>*]:relative [&>*]:z-10',
      )}
    >
      {/* Brand row — metronome + wordmark + by Stratium */}
      <button
        type="button"
        onClick={() => navigate(isPlatformAdmin ? '/platform' : '/')}
        className="flex w-full items-center gap-2.5 px-4 pb-3 pt-4 text-left hover:opacity-90"
        aria-label="Cadence home"
      >
        <Metronome size={26} className="block text-foreground" title="" />
        <div className="flex flex-col">
          <span className="text-[15px] font-bold leading-tight tracking-tight text-foreground">
            Cadence
          </span>
          <span className="text-[10px] font-medium uppercase leading-tight tracking-[0.08em] text-muted-foreground">
            by Stratium
          </span>
        </div>
      </button>

      {/* Grouped nav */}
      <nav className="flex-1 overflow-y-auto px-2 pb-2">
        {primaryGroups.map(group => (
          <div key={group.label} className="mb-3">
            <p className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              {group.label}
            </p>
            <ul className="flex flex-col gap-0.5">
              {group.items.map(item => {
                const isActive =
                  item.path === '/' || item.path === '/platform'
                    ? location.pathname === item.path
                    : location.pathname.startsWith(item.path);
                return (
                  <li key={item.path}>
                    <RailRow item={item} isActive={isActive} />
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        {overflowGroups.length > 0 && (
          <Popover open={moreOpen} onOpenChange={setMoreOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(
                  'mt-2 flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition-colors',
                  'text-muted-foreground hover:bg-accent hover:text-foreground',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  moreOpen && 'bg-accent text-foreground',
                )}
              >
                <MoreHorizontal className="h-4 w-4 shrink-0" />
                <span>More</span>
              </button>
            </PopoverTrigger>
            <PopoverContent side="right" align="start" className="w-72 max-h-[80vh] overflow-y-auto p-2">
              {overflowGroups.map(group => (
                <div key={group.label} className="mb-2 last:mb-0">
                  <p className="px-2 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    {group.label}
                  </p>
                  <ul className="flex flex-col gap-0.5">
                    {group.items.map(item => {
                      const Icon = item.icon;
                      const active = location.pathname.startsWith(item.path);
                      return (
                        <li key={item.path}>
                          <NavLink
                            to={item.path}
                            onClick={() => setMoreOpen(false)}
                            className={cn(
                              'flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm transition-colors',
                              'hover:bg-accent hover:text-foreground',
                              active
                                ? 'bg-primary/10 text-primary'
                                : 'text-foreground/80',
                            )}
                          >
                            <Icon className="h-4 w-4 shrink-0" />
                            <span className="truncate">{item.label}</span>
                          </NavLink>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </PopoverContent>
          </Popover>
        )}
      </nav>

      {/* Pinned bottom — one labelled account row. Theme, support and
          preferences live in its menu so each has a visible label
          rather than a hover-only tooltip. */}
      <div className="border-t border-border/60 p-2 dark:border-border">
        <Popover open={accountOpen} onOpenChange={setAccountOpen}>
          <PopoverTrigger asChild>
            <button
              ref={accountTriggerRef}
              type="button"
              aria-label={`Account: ${userDisplayName}${userRole ? `, ${userRole}` : ''}`}
              className="flex w-full items-center gap-2.5 rounded-lg p-2 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-accent"
            >
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary text-[13px] font-semibold text-primary-foreground">
                {userInitial}
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm font-semibold leading-tight text-foreground">
                  {userDisplayName}
                </span>
                {userRole && (
                  <span className="truncate text-xs leading-tight text-muted-foreground">{userRole}</span>
                )}
              </span>
              <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
            </button>
          </PopoverTrigger>
          <PopoverContent side="top" align="start" aria-label="Account" className="w-[236px]">
            <div className="px-2 pb-2 pt-1">
              <p className="truncate text-sm font-semibold leading-tight">{userDisplayName}</p>
              {userRole && <p className="truncate text-xs text-muted-foreground">{userRole}</p>}
            </div>
            <div className="my-1 border-t border-border/60" />
            <button
              type="button"
              onClick={() => runAccountAction(() => navigate('/preferences'))}
              className={ACCOUNT_ITEM_CLASS}
            >
              <Settings className="h-4 w-4 text-foreground/70" />
              Preferences
            </button>
            <button type="button" onClick={() => runAccountAction(onToggleTheme)} className={ACCOUNT_ITEM_CLASS}>
              <ThemeIcon className="h-4 w-4 text-foreground/70" />
              {themeLabel}
            </button>
            <button type="button" onClick={() => runAccountAction(onReportBug)} className={ACCOUNT_ITEM_CLASS}>
              <Bug className="h-4 w-4 text-foreground/70" />
              Report a problem
            </button>
            <div className="my-1 border-t border-border/60" />
            <button
              type="button"
              onClick={() => runAccountAction(onLogout)}
              className={cn(ACCOUNT_ITEM_CLASS, 'text-destructive hover:bg-destructive/10')}
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </PopoverContent>
        </Popover>
      </div>
    </aside>
  );
}

interface RailRowProps {
  item: RailItem;
  isActive: boolean;
}

function RailRow({ item, isActive }: RailRowProps) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.path}
      className={cn(
        'group relative flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-sm transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isActive
          ? 'bg-primary/10 font-semibold text-primary'
          // Full-strength label (was foreground/85) so the nav reads clearly on
          // the dark sidebar; icon lifted from muted to foreground/70 (was the
          // dim muted-foreground) for the same reason.
          : 'text-foreground hover:bg-accent hover:text-foreground',
      )}
    >
      {isActive && (
        <span
          aria-hidden
          className="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-primary"
        />
      )}
      <Icon
        className={cn(
          'h-4 w-4 shrink-0',
          isActive ? 'text-primary' : 'text-foreground/70 group-hover:text-foreground',
        )}
      />
      <span className="truncate">{item.label}</span>
    </NavLink>
  );
}
